// Sidebar.tsx
import React, { useEffect, useMemo } from 'react'
import { Badge, Box, Divider, Drawer, IconButton, List, Theme, Typography, useTheme } from '@mui/material'
import { Close as CloseIcon, SupervisorAccount as SupervisorAccountIcon, Sync as SyncIcon } from '@mui/icons-material'

import { shellHairline, sidePanelColor } from '../../theme'
import { SidebarToggleIcon } from './icons/SidebarToggleIcon'
import { SidebarMenuItem } from './SidebarMenuItem'
import { SidebarAccountFooter } from './SidebarAccountFooter'
import { MicOutlineIcon } from '../icons/MicOutlineIcon'
import medendoLogo from '../../assets/medendo_logo.png'
import medendoMark from '../../assets/medendo_mark.png'
import { useAuthStore } from '../../store/authStore'
import GroupsIcon from '@mui/icons-material/Groups'
import { useAppStore } from '../../store/appStore'
import { get, post } from '../../api/api'
import { showMessage } from '../../utils/snackbar'
import { useLocation, useNavigate } from 'react-router-dom'
import MicIcon from '@mui/icons-material/Mic'
import HistoryIcon from '@mui/icons-material/History'
import DescriptionIcon from '@mui/icons-material/Description'
import BugReportIcon from '@mui/icons-material/BugReport'
// Outlined counterparts of the icons above: Material's own nav guidance is an outlined glyph
// for an unselected destination, switching to the filled glyph only once it's the active one —
// a second, non-color channel for "you are here" that doesn't depend on the selected-state tint.
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import { openIntercomMessenger } from '../../hooks/useIntercom'
import { ROUTES } from '../../routes'
import { useIsUserFromMedendoTeam } from '../../hooks/useIsUserFromMedendoTeam'
import { useHasPermission } from '../../hooks/useHasPermission'
import { isReadOnly } from '../../models/UserModel'
import { useTranslation } from 'react-i18next'
import { headerHeight } from './shellSizes'

interface SidebarProps {
    drawerWidth: number
    collapsedWidth: number
    onLogoutClick: () => void
    open: boolean
    onToggle: () => void
    isMobile: boolean
}

const drawerStyles = (open: boolean, drawerWidth: number, collapsedWidth: number, theme: Theme) => ({
    width: open ? drawerWidth : collapsedWidth,
    flexShrink: 0,
    // This outer width is what actually reserves the flex row's space next to main — the
    // paper below only transitions its own visual width, which browsers don't propagate to
    // siblings on its own. Animating this one is what makes main's reflow follow smoothly
    // instead of snapping the instant this width's final value changes.
    transition: theme.transitions.create(['width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    '& .MuiDrawer-paper': {
        width: open ? drawerWidth : collapsedWidth,
        boxSizing: 'border-box',
        borderRadius: '0',
        borderRight: '1px solid',
        borderColor: shellHairline,
        padding: '0',
        // A shade darker than the main content, so the side panels frame it
        backgroundColor: sidePanelColor,
        overflowX: 'hidden',
        // 100% resolves against the large (chrome-collapsed) viewport on phones, hiding the
        // drawer bottom behind the URL bar; dvh tracks the actually-visible height instead.
        height: '100dvh',
        transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        display: 'flex',
        flexDirection: 'column',
    },
})

const BRAND_NAME = 'Medendo'

// The toggle in its expand look: the mark gives way to the expand icon with its arrow, on a hover tint
const expandLookSx = {
    '& .sidebar-mark': { display: 'none' },
    '& .sidebar-mark + .sidebar-toggle': { display: 'flex' },
    '& .sidebar-toggle-arrow': { opacity: 1 },
    bgcolor: 'action.hover',
} as const

// On the collapsed rail the toggle shows the mark, and turns into the expand icon on hover or
// keyboard focus, which is what it does
const collapsedMarkSx = {
    '& .sidebar-mark + .sidebar-toggle': { display: 'none' },
    '&:hover, &:focus-visible': expandLookSx,
} as const

// Like the collapsed chat rail, the whole collapsed sidebar expands it. Hovering its empty space
// shows that: an expand cursor, and the toggle in its expand look. Its own items keep their hover.
const collapsedRailSx = {
    cursor: 'e-resize',
    '&:hover:not(:has(.MuiButtonBase-root:hover)) .sidebar-toggle-button': expandLookSx,
} as const

// A click on the rail itself, not on one of its items or buttons
function isRailBackgroundClick(event: React.MouseEvent): boolean {
    return event.target instanceof Element && !event.target.closest('.MuiButtonBase-root')
}

export const Sidebar: React.FC<SidebarProps> = (props: SidebarProps) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const isUserFromMedendoTeam = useIsUserFromMedendoTeam()
    const payload = useAuthStore((state) => state.payload)
    const orgModel = useAppStore((state) => state.orgModel)
    const groups = useAppStore((state) => state.groups)
    const canCreateReports = useHasPermission('can_create_reports')
    const canManageUsersInOrg = useHasPermission('org_admin') && orgModel !== null
    // Org admins manage every group; group admins see the tab for the groups they administer.
    // Plain members reach their groups through Settings → Your groups.
    const canSeeGroups = canManageUsersInOrg || groups.some((g) => g.role === 'admin' || g.role === 'template_admin')
    const userModel = useAppStore((state) => state.userModel)
    const isOffboarded = isReadOnly(userModel)
    const isSoloUser = userModel !== null && userModel.org_id === null
    const isIntercomReady = useAppStore((state) => state.isIntercomReady)
    const intercomUnreadCount = useAppStore((state) => state.intercomUnreadCount)
    const frontendVersion = import.meta.env.VITE_VERSION ?? 'dev'
    const [backendVersion, setBackendVersion] = React.useState<string | null>(null)
    const location = useLocation()

    const navigate = useNavigate()

    const translatedMenu = useMemo(
        () => ({
            NewEncounter: {
                name: t('encounter.menu_record'),
                icon: <MicOutlineIcon />,
                iconActive: <MicIcon />,
                click_navigate: () => `${ROUTES.encounters.new.path}?ts=${Date.now()}`,
                route: ROUTES.encounters.new.path,
            },
            History: {
                name: t('encounter.menu_history'),
                icon: <HistoryOutlinedIcon />,
                iconActive: <HistoryIcon />,
                click_navigate: () => ROUTES.encounters.list.path,
                route: ROUTES.encounters.list.path,
            },
            Templates: {
                name: t('encounter.menu_templates'),
                icon: <DescriptionOutlinedIcon />,
                iconActive: <DescriptionIcon />,
                click_navigate: () => ROUTES.templates.list.path,
                route: ROUTES.templates.list.path,
            },
            Admin: {
                name: t('encounter.menu_admin'),
                icon: <SupervisorAccountOutlinedIcon />,
                iconActive: <SupervisorAccountIcon />,
                click_navigate: () => ROUTES.admin.users.path,
                route: ROUTES.admin.users.path,
            },
            Groups: {
                name: t('encounter.menu_groups'),
                icon: <GroupsOutlinedIcon />,
                iconActive: <GroupsIcon />,
                click_navigate: () => ROUTES.groups.list.path,
                route: ROUTES.groups.list.path,
            },
            Integrations: {
                name: t('encounter.menu_integrations'),
                icon: <SyncOutlinedIcon />,
                iconActive: <SyncIcon />,
                click_navigate: () => ROUTES.integrations.list.path,
                route: ROUTES.integrations.list.path,
            },
        }),
        [t]
    )

    // Most-specific match wins: /e/new should not activate /e
    const activeMenuItem = useMemo(() => {
        const matches = Object.values(translatedMenu).filter(
            (item) => location.pathname === item.route || location.pathname.startsWith(item.route + '/')
        )
        if (!matches.length) return null
        return matches.reduce((best, curr) => (curr.route.length > best.route.length ? curr : best), matches[0])
    }, [location.pathname, translatedMenu])

    useEffect(() => {
        const fetchVersion = async () => {
            try {
                const version = await get<string>('/version')
                setBackendVersion(version)
                console.info('[Sidebar] Backend version: ', version)
            } catch (error) {
                console.error('Failed to fetch version:', error)
            }
        }
        fetchVersion()
        console.info('[Sidebar] Frontend version: ', import.meta.env.VITE_VERSION)
    }, [])

    // Drives the portal menu item's disabled state and guards against a double-click
    // minting a second Stripe billing_portal.Session request while the first is in flight.
    const [isOpeningPortal, setIsOpeningPortal] = React.useState(false)

    const handleOpenBillingPortal = async () => {
        if (isOpeningPortal) return
        setIsOpeningPortal(true)
        // Opened synchronously, still inside the click, and navigated once the URL arrives:
        // opening the tab only after the awaited POST below resolves would no longer count
        // as user-initiated, so browsers (Safari in particular) would block it as a popup.
        // noopener/noreferrer would make window.open() return null, leaving nothing to
        // navigate later, so the opener link is severed by hand instead, right below.
        const portalWindow = window.open('', '_blank')
        if (portalWindow) portalWindow.opener = null

        if (!portalWindow) {
            // Already blocked before we even had a URL — no point minting a Stripe session
            // nobody can reach.
            showMessage(t('encounter.customer_portal_error'), 'error')
            setIsOpeningPortal(false)
            return
        }

        try {
            // retries: 0 — matches AccountStatus.tsx's own renewal call: a silently
            // auto-retried POST here shouldn't be the frontend's default behavior for a
            // user-triggered navigation.
            const { url } = await post<{ url: string }>('/me/billing-portal-session', {}, 0, 1000)
            portalWindow.location.href = url
        } catch (error) {
            console.warn('Failed to start a billing portal session:', error)
            showMessage(t('encounter.customer_portal_error'), 'error')
            portalWindow.close()
        } finally {
            setIsOpeningPortal(false)
        }
    }

    // Mobile's drawer is an overlay, always shown expanded — no collapsed rail state there.
    const isOpen = props.isMobile || props.open

    // Extra room for the logo next to mobile's close button.
    const mobileDrawerWidth = Math.round(props.drawerWidth * 1.2)

    // Only animates labels in after a real collapse, not on first mount (nothing to wait for then).
    const [hasExpanded, setHasExpanded] = React.useState(false)
    if (props.open && !hasExpanded) setHasExpanded(true)
    const animateLabel = hasExpanded && !isOpen

    // Collapsing can snap labels immediately (harmless mid-transition); expanding can't — full
    // labels would visibly wrap for an instant before the drawer's width catches up, so that
    // case waits for the ResizeObserver below instead. Desktop only; mobile has nothing to collapse.
    const [labelsExpanded, setLabelsExpanded] = React.useState(isOpen)
    if ((props.isMobile || !props.open) && labelsExpanded !== isOpen) setLabelsExpanded(isOpen)
    const drawerPaperRef = React.useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        // Tied to the drawer's measured width, not a guessed timer, so it can't drift out of
        // sync. Flips at 80% open, not 100% — a clean word-boundary wrap for the last stretch is fine.
        if (props.isMobile || !props.open || !drawerPaperRef.current) return
        const threshold = props.collapsedWidth + (props.drawerWidth - props.collapsedWidth) * 0.8
        const el = drawerPaperRef.current
        const observer = new ResizeObserver(([entry]) => {
            if (entry.contentRect.width >= threshold) setLabelsExpanded(true)
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [props.open, props.isMobile, props.collapsedWidth, props.drawerWidth])

    // On mobile the drawer overlays the page it navigated to, so close it after selecting.
    const handleNavigate = (path: string) => {
        navigate(path)
        if (props.isMobile) props.onToggle()
    }

    // On mobile the drawer would cover the messenger it just opened.
    const handleSupport = () => {
        openIntercomMessenger()
        if (props.isMobile) props.onToggle()
    }

    return (
        <Drawer
            sx={drawerStyles(
                isOpen,
                props.isMobile ? mobileDrawerWidth : props.drawerWidth,
                props.collapsedWidth,
                theme
            )}
            variant={props.isMobile ? 'temporary' : 'permanent'}
            anchor="left"
            open={props.open}
            onClose={props.onToggle}
            ModalProps={{
                keepMounted: true,
            }}
            slotProps={{ paper: { ref: drawerPaperRef } }}
        >
            <Box
                // A mouse shortcut only; the toggle button stays the way in for keyboards
                onClick={!isOpen ? (event) => isRailBackgroundClick(event) && props.onToggle() : undefined}
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    height: '100%',
                    ...(!isOpen && collapsedRailSx),
                }}
            >
                {props.isMobile && (
                    // The content pane's own logo (see Toolbar/AppToolbar.tsx) lives behind this overlay
                    // while it's open, so the drawer needs its own — right in the row with
                    // the close button, not floating separately above reserved empty space.
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: 52,
                            px: 2,
                        }}
                    >
                        <img src={medendoLogo} alt={BRAND_NAME} width={120} style={{ display: 'block' }} />
                        <IconButton
                            onClick={props.onToggle}
                            aria-label={t('encounter.menu_close')}
                            sx={{ width: 36, height: 36, borderRadius: '8px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                )}
                {/* Top section - grows to push content down. Its first row carries the logo: on
                    the collapsed rail the mark alone is the toggle, and the full logo sits beside
                    the toggle when open, so the brand stays in one place whatever the page. */}
                <Box sx={{ flex: 1 }}>
                    {!props.isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: headerHeight,
                            }}
                        >
                            {/* A fixed-width lane matching the collapsed rail, not centered
                                across the row's own full (collapsed-or-expanded) width —
                                otherwise the button visibly jumps to the middle of a 220px row
                                once expanded, instead of staying where the icon column below
                                it always is. */}
                            <Box
                                sx={{
                                    width: props.collapsedWidth,
                                    flexShrink: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <IconButton
                                    onClick={props.onToggle}
                                    aria-label={t('encounter.menu_open')}
                                    aria-expanded={isOpen}
                                    className="sidebar-toggle-button"
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '8px',
                                        ...(!isOpen && collapsedMarkSx),
                                    }}
                                >
                                    {!isOpen && (
                                        <img className="sidebar-mark" src={medendoMark} alt="" width={24} height={24} />
                                    )}
                                    <Box className="sidebar-toggle" sx={{ display: 'flex' }}>
                                        <SidebarToggleIcon open={isOpen} />
                                    </Box>
                                </IconButton>
                            </Box>
                            {isOpen && (
                                <img src={medendoLogo} alt={BRAND_NAME} width={110} style={{ display: 'block' }} />
                            )}
                        </Box>
                    )}
                    <List>
                        {Object.values(translatedMenu).map((item) => {
                            // An offboarded user reaches nothing but their own history, read-only (MD-1497).
                            if (isOffboarded && item.name !== translatedMenu.History.name) return null
                            if (item.name === translatedMenu.NewEncounter.name && !canCreateReports) return null
                            if (item.name === translatedMenu.Admin.name && !canManageUsersInOrg) return null
                            if (item.name === translatedMenu.Groups.name && !canSeeGroups) return null
                            if (item.name === translatedMenu.Integrations.name && !orgModel?.ehr_adapter) return null
                            return (
                                <SidebarMenuItem
                                    key={item.name}
                                    item={item}
                                    labelsExpanded={labelsExpanded}
                                    animateLabel={animateLabel}
                                    isActive={item === activeMenuItem}
                                    onClick={() => handleNavigate(item.click_navigate())}
                                />
                            )
                        })}

                        {/* Deliberately not hidden from an offboarded user, unlike everything else
                            above: the portal is where a lapsed solo subscriber reviews their
                            invoices and payment method, so it is more relevant to them than to
                            an active one, not less (MD-1497). */}
                        {isSoloUser && (
                            <>
                                <Divider sx={{ mt: '8px' }} />
                                <SidebarMenuItem
                                    item={{
                                        name: t('encounter.customer_portal'),
                                        icon: <PaymentsOutlinedIcon />,
                                        click_navigate: () => '',
                                        route: '',
                                    }}
                                    labelsExpanded={labelsExpanded}
                                    animateLabel={animateLabel}
                                    isActive={false}
                                    onClick={handleOpenBillingPortal}
                                    disabled={isOpeningPortal}
                                />
                            </>
                        )}
                        {isUserFromMedendoTeam && !isOffboarded && (
                            <>
                                <Divider sx={{ mt: '32px' }} />
                                <Typography align="right" fontSize="8px" fontWeight="300" width="100%">
                                    {frontendVersion} / {backendVersion}
                                </Typography>

                                <Divider sx={{ mb: '8px' }} />
                                <SidebarMenuItem
                                    item={{
                                        name: 'Dev',
                                        icon: <BugReportOutlinedIcon />,
                                        iconActive: <BugReportIcon />,
                                        click_navigate: () => ROUTES.dev.panel.path,
                                        route: ROUTES.dev.panel.path,
                                    }}
                                    labelsExpanded={labelsExpanded}
                                    animateLabel={animateLabel}
                                    isActive={location.pathname.startsWith(ROUTES.dev.panel.path)}
                                    onClick={() => handleNavigate(ROUTES.dev.panel.path)}
                                />
                            </>
                        )}
                    </List>
                </Box>

                {/* Bottom section - fixed at bottom */}
                <Box sx={{ mt: 'auto' }}>
                    <List>
                        {isIntercomReady && (
                            <SidebarMenuItem
                                item={{
                                    name: t('encounter.menu_support'),
                                    icon: (
                                        <Badge badgeContent={intercomUnreadCount} color="error">
                                            <SupportAgentOutlinedIcon />
                                        </Badge>
                                    ),
                                    click_navigate: () => '',
                                    route: '',
                                }}
                                labelsExpanded={labelsExpanded}
                                animateLabel={animateLabel}
                                isActive={false}
                                onClick={handleSupport}
                            />
                        )}
                        <SidebarMenuItem
                            item={{
                                name: t('encounter.logout'),
                                icon: <LogoutOutlinedIcon />,
                                click_navigate: () => '',
                                route: '',
                            }}
                            labelsExpanded={labelsExpanded}
                            animateLabel={animateLabel}
                            isActive={false}
                            onClick={props.onLogoutClick}
                        />
                    </List>

                    <SidebarAccountFooter
                        labelsExpanded={labelsExpanded}
                        userModel={userModel}
                        email={payload?.email ?? ''}
                        selected={location.pathname.startsWith(ROUTES.settings.main.path)}
                        onClick={() => handleNavigate(ROUTES.settings.main.path)}
                        settingsLabel={t('encounter.menu_settings')}
                        versionLabel={isUserFromMedendoTeam ? `${frontendVersion} / ${backendVersion}` : undefined}
                    />
                </Box>
            </Box>
        </Drawer>
    )
}
